const makerHandle = 'tuanty-pet-fresh-food-maker';
const kitHandle = 'tuanty-all-in-one-pet-food-maker-full-kit-main-machine-glass-bowl-replacement-blades-slow-feeder-bowl';
const makerProductId = '8719340306492';
const kitProductId = '8984874745916';
const defaultOfferTitle = 'Food Maker Add-on · 20% Off';
const offerProductIds = new Set(['8858129432636', '8858129793084', '8858130317372', '8858131005500',
  '9051852505148', '15407248474172', '15408397615164', '15408419995708', '15408420028476']);
const applicationSnapshot = (application) => [application?.type, application?.title, application?.total_allocated_amount,
  application?.value_type, application?.value, application?.target_type, application?.allocation_method, application?.target_selection];
const cartSnapshot = (cart) => JSON.stringify([cart.total_price, cart.total_discount,
  (cart.discount_codes || []).map((code) => [code.code, code.applicable]),
  (cart.cart_level_discount_applications || []).map(applicationSnapshot),
  (cart.discount_applications || []).map(applicationSnapshot),
  (cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price,
    (item.line_level_discount_allocations || []).map((allocation) => [allocation.amount, applicationSnapshot(allocation.discount_application)])])]);

export function promotionState(cart, enabled, offerTitle = defaultOfferTitle) {
  const makerQuantity = (cart.items || []).filter((item) => String(item.product_id) === makerProductId)
    .reduce((quantity, item) => quantity + Math.max(0, Number(item.quantity) || 0), 0);
  const ownOffer = (application) => application?.title === offerTitle && (application.type === 'automatic' ||
    (application.type === '' && application.value_type === 'percentage' && Number(application.value) === 20 &&
      application.target_type === 'line_item' && application.allocation_method === 'each' && application.target_selection === 'entitled'));
  const eligibleItems = (cart.items || []).filter((item) => offerProductIds.has(String(item.product_id)) && Number(item.quantity) > 0);
  const confirmed = eligibleItems.every((item) => (item.line_level_discount_allocations || [])
    .some((allocation) => Number(allocation.amount) > 0 && ownOffer(allocation.discount_application)));
  const status = !enabled ? 'offer_checking' : !makerQuantity ? 'offer_no_maker' : !confirmed ? 'offer_unavailable' : 'offer_available';
  return { makerQuantity, status, available: status === 'offer_available' };
}

export function candidateOfferAvailable(cart, candidate, enabled, offerTitle = defaultOfferTitle) {
  return (candidate.offerEligible === true || candidate.offerEligible === 'true') &&
    offerProductIds.has(String(candidate.productId)) && promotionState(cart, enabled, offerTitle).available;
}

const groupOrder = ['nutrition', 'accessories', 'bowls'];
function rank(candidate, seed) {
  const key = `${seed}:${candidate.handle || candidate.productId}`;
  let value = 2166136261;
  for (const character of key) value = Math.imul(value ^ character.charCodeAt(0), 16777619);
  return value >>> 0;
}

export function recommendationGroups(cart, candidates, seed = 0) {
  const groups = { nutrition: [], accessories: [], bowls: [] };
  const items = cart.items || [];
  if (!items.length) return groups;
  const purchasedProducts = new Set(items.map((item) => String(item.product_id)));
  const purchasedVariants = new Set(items.map((item) => String(item.variant_id || item.id)));
  const handles = new Set(items.map((item) => item.handle));
  const hasMaker = purchasedProducts.has(makerProductId) || handles.has(makerHandle);
  const hasKit = purchasedProducts.has(kitProductId) || handles.has(kitHandle);
  const seenProducts = new Set();
  const available = candidates.filter((item) => {
    const id = String(item.productId);
    if (!item.available || !item.productId || !item.variantId || seenProducts.has(id) ||
        purchasedProducts.has(id) || purchasedVariants.has(String(item.variantId))) return false;
    seenProducts.add(id);
    return true;
  });
  groups.nutrition = available.filter((item) => ['premix', 'daily'].includes(item.kind) &&
    ['cat', 'dog'].includes(item.species));
  if (hasMaker && !hasKit) groups.accessories = available.filter((item) => item.kind === 'accessory');
  if (!hasKit) groups.bowls = available.filter((item) => item.kind === 'bowl' && item.species === 'dog');
  const priority = hasMaker || hasKit ? 'premix' : 'daily';
  for (const [group, products] of Object.entries(groups)) {
    products.sort((a, b) => {
      const relevance = group === 'nutrition' ? Number(b.kind === priority) - Number(a.kind === priority) : 0;
      return relevance || rank(a, seed) - rank(b, seed) || String(a.variantId).localeCompare(String(b.variantId));
    });
  }
  return groups;
}

export function eligibleRecommendations(cart, candidates, seed = 0) {
  return Object.values(recommendationGroups(cart, candidates, seed)).flat();
}

export function cartContains(cart, productId, variantId) {
  return (cart.items || []).some((item) => String(item.product_id) === String(productId) ||
    String(item.variant_id || item.id) === String(variantId));
}

if (typeof customElements !== 'undefined' && !customElements.get('tuanty-cart-recommendations')) {
  const drawerState = new WeakMap();
  let adding = false;
  let nativeUpdating = false;
  let nativeIntentTimer = null;
  let nativeWatchdog = null;
  let latestCart;
  let cartRevision = 0;
  let hydration;

  document.addEventListener('theme:loading:done', () => {
    clearTimeout(nativeWatchdog);
    nativeUpdating = false;
  });

  document.addEventListener('cart:change', (event) => {
    if (!Array.isArray(event.detail?.cart?.items)) return;
    latestCart = event.detail.cart;
    cartRevision += 1;
    document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => {
      element.cart = latestCart;
      element.offerReady = true;
      element.renderOffer();
    });
  }, true);

  class TuantyCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.drawer = this.closest('hdt-cart-drawer');
      if (!this.drawer) return;
      try {
        this.cart = JSON.parse(this.querySelector('[data-tuanty-cart]').textContent);
        const translated = JSON.parse(this.querySelector('[data-tuanty-strings]').textContent);
        const decoder = document.createElement('textarea');
        this.strings = Object.fromEntries(Object.entries(translated).map(([key, value]) => {
          // Liquid translation output may contain entities inside JSON; visible output remains textContent.
          decoder.innerHTML = String(value);
          return [key, decoder.value];
        }));
      } catch { this.hidden = true; return; }
      this.offerEnabled = this.dataset.offerEnabled === 'true';
      this.offerTitle = this.dataset.offerTitle || defaultOfferTitle;
      this.offerReady = Boolean(latestCart && cartSnapshot(latestCart) === cartSnapshot(this.cart));
      if (this.offerReady) this.cart = latestCart;
      this.candidates = [...this.querySelectorAll('[data-tuanty-candidate]')].map((template) => ({
        ...template.dataset, available: template.dataset.available === 'true', template
      }));
      this.state = drawerState.get(this.drawer);
      if (!this.state) {
        const seed = globalThis.crypto?.getRandomValues ? crypto.getRandomValues(new Uint32Array(1))[0] : Math.floor(Math.random() * 4294967296);
        this.state = { locked: false, seed, anchors: {} };
        drawerState.set(this.drawer, this.state);
      }
      this.groups = [...this.querySelectorAll('[data-tuanty-group]')].map((element) => ({
        element, key: element.dataset.tuantyGroup, track: element.querySelector('[data-tuanty-track]')
      }));
      const listener = { signal: this.controller.signal };
      this.addEventListener('submit', (event) => this.add(event), { ...listener, capture: true });
      for (const group of this.groups) {
        group.track.addEventListener('scroll', () => this.updateNavigation(group), { ...listener, passive: true });
        group.track.addEventListener('keydown', (event) => this.handleTrackKey(group, event), listener);
      }
      this.resizeObserver = new ResizeObserver(() => this.groups.forEach((group) => this.restorePosition(group)));
      this.groups.forEach((group) => this.resizeObserver.observe(group.track));
      this.drawer.addEventListener('dialog:afterOpen', () => this.groups.forEach((group) => this.restorePosition(group)), listener);
      document.addEventListener('theme:loading:start', () => this.setButtons(true), listener);
      document.addEventListener('theme:loading:doing', (event) => {
        nativeUpdating = true;
        this.setButtons(true);
        clearTimeout(nativeWatchdog);
        if (event.detail?.tuantyControls) return;
        nativeWatchdog = setTimeout(() => {
          if (!nativeUpdating) return;
          nativeUpdating = false;
          document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => {
            element.state.locked = true;
            element.hidden = false;
            element.state.notice = element.strings.uncertain;
            element.showStatus(element.strings.uncertain);
            element.querySelector('[data-tuanty-review]').hidden = false;
            element.setButtons(false);
          });
        }, 20000);
      }, listener);
      document.addEventListener('theme:loading:done', () => this.setButtons(adding || nativeIntentTimer !== null), listener);
      this.render();
      if (this.offerEnabled && !this.offerReady && !hydration) {
        const revision = cartRevision;
        hydration = this.getJSON(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cart) => {
          if (revision !== cartRevision || !Array.isArray(cart.items)) return;
          latestCart = cart;
          document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => {
            element.cart = cart;
            element.offerReady = true;
            element.render();
          });
        }).catch(() => {}).finally(() => { hydration = null; });
      }
      if (this.state.notice) { this.showStatus(this.state.notice); this.state.notice = ''; }
      if (this.state.locked) this.querySelector('[data-tuanty-review]').hidden = false;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    render() {
      const candidates = recommendationGroups(this.cart, this.candidates, this.state.seed);
      this.hidden = !Object.values(candidates).some((group) => group.length) && !this.state.locked;
      for (const group of this.groups) {
        const fragment = document.createDocumentFragment();
        for (const candidate of candidates[group.key]) fragment.append(candidate.template.content.cloneNode(true));
        group.track.replaceChildren(fragment);
        group.element.hidden = !candidates[group.key].length;
        this.restorePosition(group);
      }
      this.renderOffer();
      this.setButtons(adding || nativeUpdating || nativeIntentTimer !== null);
    }

    renderOffer() {
      if (!this.candidates || !this.strings) return;
      const offerBlock = this.querySelector('[data-tuanty-offer]');
      if (offerBlock) offerBlock.hidden = !(this.offerReady && promotionState(this.cart, this.offerEnabled, this.offerTitle).available);
      const status = this.querySelector('[data-tuanty-offer-status]');
      if (status) status.textContent = this.strings[this.offerReady ? promotionState(this.cart, this.offerEnabled, this.offerTitle).status : 'offer_checking'];
      this.querySelectorAll('[data-tuanty-add]').forEach((form) => {
        const candidate = this.candidates.find((item) => String(item.variantId) === String(form.elements.id.value));
        const shown = Boolean(this.offerReady && candidate && candidateOfferAvailable(this.cart, candidate, this.offerEnabled, this.offerTitle));
        const card = form.closest('[data-tuanty-handle]');
        const regular = card?.querySelector('[data-tuanty-price-regular]');
        const offer = card?.querySelector('[data-tuanty-price-offer]');
        const badge = card?.querySelector('[data-tuanty-offer-badge]');
        if (!regular || !offer) return;
        regular.hidden = shown;
        offer.hidden = !shown;
        if (badge) badge.hidden = !shown;
        form.dataset.offerShown = String(shown);
      });
    }

    restorePosition(group) {
      if (group.element.hidden || !group.track.clientWidth) return;
      const cards = [...group.track.children];
      const anchor = this.state.anchors[group.key];
      const card = cards.find((item) => item.dataset.tuantyHandle === anchor?.handle) || cards[Math.min(anchor?.index || 0, cards.length - 1)];
      if (card) group.track.scrollLeft = card.offsetLeft - cards[0].offsetLeft;
      this.updateNavigation(group);
    }

    updateNavigation(group) {
      const track = group.track;
      if (group.element.hidden || !track.clientWidth) return;
      const position = Math.abs(track.scrollLeft);
      const cards = [...track.children];
      const index = cards.reduce((best, card, at) => Math.abs(card.offsetLeft - cards[0].offsetLeft - position) <
        Math.abs(cards[best].offsetLeft - cards[0].offsetLeft - position) ? at : best, 0);
      if (cards[index]) this.state.anchors[group.key] = { handle: cards[index].dataset.tuantyHandle, index };
    }

    move(group, direction) {
      const card = group.track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(group.track).columnGap) || 0;
      group.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'auto' });
    }

    handleTrackKey(group, event) {
      if (event.target !== group.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') group.track.scrollTo({ left: event.key === 'Home' ? 0 : group.track.scrollWidth, behavior: 'auto' });
      else this.move(group, event.key === 'ArrowLeft' ? -1 : 1);
    }

    restoreFocus() {
      const intended = this.state.focus;
      if (!intended) return;
      this.state.focus = null;
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const group = this.groups.find((item) => item.key === intended.group);
        const buttons = group ? [...group.track.querySelectorAll('[data-tuanty-add] button:not(:disabled)')] : [];
        const laterGroups = [...this.groups.slice(groupOrder.indexOf(intended.group) + 1), ...this.groups];
        const button = buttons[Math.min(intended.index, buttons.length - 1)] || laterGroups
          .filter((item) => !item.element.hidden).flatMap((item) => [...item.track.querySelectorAll('[data-tuanty-add] button:not(:disabled)')])[0];
        const target = this.state.locked ? this.querySelector('[data-tuanty-review]') : button ||
          this.drawer.querySelector('[name="checkout"], .hdt-mini-cart__emty-button') ||
          this.drawer.shadowRoot?.querySelector('[part="close-button"]');
        target?.focus({ preventScroll: true });
        target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }

    setButtons(busy) {
      this.querySelectorAll('[data-tuanty-add] button').forEach((button) => { button.disabled = busy || this.state.locked; });
      if (!busy) this.restoreFocus();
    }

    showStatus(message) {
      const status = this.querySelector('[data-tuanty-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }

    async getJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
        ...options,
        headers: { Accept: 'application/json', ...options.headers }
      });
      if (!response.ok) throw new Error('cart_request_failed');
      return response.json();
    }

    async refreshCart(cart, sections, sectionIds, addedItems) {
      if (!sections?.[this.dataset.sectionId]) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        // Product URLs negotiate product JSON for application/json; sections use the normal page request.
        sections = await this.getJSON(url.toString(), { headers: { Accept: '*/*' } });
      }
      if (!sections?.[this.dataset.sectionId]) throw new Error('cart_render_failed');
      cart.sections = sections;
      if (addedItems) this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true, detail: { items: addedItems, actionAfterATC: 'open_cart_drawer', cart }
      }));
      document.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: addedItems ? 'variant:add' : 'line-item:change', actionAfterATC: 'open_cart_drawer', cart }
      }));
    }

    async add(event) {
      const form = event.target.closest('[data-tuanty-add]');
      if (!form) return;
      event.preventDefault();
      if (adding || nativeUpdating || nativeIntentTimer !== null || this.state.locked) return;
      adding = true;
      const group = form.closest('[data-tuanty-group]');
      this.state.focus = { group: group.dataset.tuantyGroup, index: [...group.querySelectorAll('[data-tuanty-add]')].indexOf(form) };
      this.setButtons(true);
      this.showStatus(this.strings.adding);
      const protectedNodes = [...this.drawer.querySelectorAll('.hdt-mini-cart__items, .hdt-mini-cart__bottom')]
        .map((node) => ({ node, inert: node.inert }));
      protectedNodes.forEach(({ node }) => { node.inert = true; });
      const root = window.Shopify?.routes?.root || '/';
      const variantId = form.elements.id.value;
      const productId = form.dataset.productId;
      const offerShown = form.dataset.offerShown === 'true';
      const sectionIds = [this.dataset.sectionId];
      document.dispatchEvent(new CustomEvent('cart:push:section.id', { detail: { sections: sectionIds } }));
      const sections = [...new Set(sectionIds)].slice(0, 5);
      let posted = false;
      try {
        const before = await this.getJSON(`${root}cart.js`);
        if (cartContains(before, productId, variantId)) {
          this.state.notice = this.strings.already_added;
          await this.refreshCart(before, null, sections);
          return;
        }
        const candidate = eligibleRecommendations(before, this.candidates, this.state.seed).find((item) => String(item.variantId) === String(variantId));
        if (!candidate) {
          this.state.notice = this.strings.error;
          await this.refreshCart(before, null, sections);
          return;
        }
        if (offerShown && (!this.offerEnabled || !promotionState(before, this.offerEnabled, this.offerTitle).makerQuantity)) {
          this.state.notice = this.strings.offer_changed;
          await this.refreshCart(before, null, sections);
          return;
        }
        posted = true;
        const added = await this.getJSON(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname })
        });
        const cart = await this.getJSON(`${root}cart.js`);
        this.state.notice = this.strings.added;
        await this.refreshCart(cart, added.sections, sections, added.items || [added]);
      } catch {
        if (posted) {
          try {
            const cart = await this.getJSON(`${root}cart.js`);
            this.state.notice = cartContains(cart, productId, variantId) ? this.strings.added : this.strings.error;
            await this.refreshCart(cart, null, sections);
          } catch {
            this.state.locked = true;
            this.hidden = false;
            this.showStatus(this.strings.uncertain);
            this.querySelector('[data-tuanty-review]').hidden = false;
          }
        } else {
          this.showStatus(this.strings.error);
        }
      } finally {
        adding = false;
        protectedNodes.forEach(({ node, inert }) => { node.inert = inert; });
        document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => element.setButtons(false));
        document.dispatchEvent(new CustomEvent('theme:loading:done', { bubbles: true }));
      }
    }
  }

  // Capture user intent before native controls replace their section; ignore reconnect initialization.
  function rememberNativeFocus(event) {
    const target = event.target.closest?.('.hdt-mini-cart__remove, .hdt-quantity__button, [name="updates[]"]');
    if (!target || (event.type === 'click' && target.matches('[name="updates[]"]')) || (event.type === 'change' && (!event.isTrusted || !target.matches('[name="updates[]"]')))) return;
    const row = target.closest('.hdt-mini-cart__item');
    const drawer = row?.closest('hdt-cart-drawer.tuanty-review-cart');
    const state = drawer && drawerState.get(drawer);
    if (!state) return;
    // Native quantity changes debounce for 300ms. Block a competing recommendation add immediately.
    clearTimeout(nativeIntentTimer);
    nativeIntentTimer = setTimeout(() => {
      nativeIntentTimer = null;
      document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => element.setButtons(adding || nativeUpdating));
    }, 450);
    document.querySelectorAll('tuanty-cart-recommendations').forEach((element) => element.setButtons(true));
    state.nativeFocus = {
      index: [...drawer.querySelectorAll('.hdt-mini-cart__item')].indexOf(row),
      remove: target.matches('.hdt-mini-cart__remove'),
      button: target.matches('.hdt-quantity__button') ? event.detail === 0 ? target.name : 'pointer' : null
    };
  }
  document.addEventListener('click', rememberNativeFocus, true);
  document.addEventListener('change', rememberNativeFocus, true);
  document.addEventListener('cart:drawer:render', () => {
    for (const drawer of document.querySelectorAll('hdt-cart-drawer.tuanty-review-cart')) {
      const state = drawerState.get(drawer);
      const intended = state?.nativeFocus;
      if (intended) {
        state.nativeFocus = null;
        requestAnimationFrame(() => {
          if (intended.button === 'pointer' || !drawer.hasAttribute('open') || (document.activeElement !== document.body && !drawer.contains(document.activeElement))) return;
          const rows = [...drawer.querySelectorAll('.hdt-mini-cart__item')];
          const row = rows[Math.min(intended.index, rows.length - 1)];
          const target = row?.querySelector(intended.remove ? '.hdt-mini-cart__remove' : intended.button ? `.hdt-quantity__button[name="${intended.button}"]` : '[name="updates[]"]') ||
            drawer.querySelector('[data-tuanty-group]:not([hidden]) [data-tuanty-add] button:not(:disabled), [name="checkout"], .hdt-mini-cart__emty-button') ||
            drawer.shadowRoot?.querySelector('[part="close-button"]');
          target?.focus({ preventScroll: true });
          target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }
      if (!drawer.querySelector('.hdt-mini-cart__item')) drawerState.delete(drawer);
    }
  });
  customElements.define('tuanty-cart-recommendations', TuantyCartRecommendations);
}

